/** A tile position on a half-tile grid: a tile is 2 wide and 2 tall, so x and y of neighbouring tiles differ by 2. */
export interface Pos {
  readonly x: number;
  readonly y: number;
  readonly layer: number;
}

export interface Layout {
  readonly id: string;
  readonly name: string;
  readonly positions: readonly Pos[];
}

const range = (from: number, to: number, step = 2): number[] => {
  const out: number[] = [];
  for (let v = from; v <= to; v += step) out.push(v);
  return out;
};
const grid = (xs: number[], ys: number[], layer: number): Pos[] => ys.flatMap((y) => xs.map((x) => ({ x, y, layer })));

/** The classic "turtle": 144 tiles over five layers (87 + 36 + 16 + 4 + 1). */
function turtle(): Pos[] {
  const rows: [number, number, number][] = [
    [0, 2, 24],
    [2, 6, 20],
    [4, 4, 22],
    [6, 2, 24],
    [8, 2, 24],
    [10, 4, 22],
    [12, 6, 20],
    [14, 2, 24],
  ];
  const layer0 = rows.flatMap(([y, from, to]) => range(from, to).map((x) => ({ x, y, layer: 0 })));
  // The two side tiles on the left and the one on the right sit between rows, plus the far right tile.
  layer0.push({ x: 0, y: 7, layer: 0 }, { x: 26, y: 7, layer: 0 }, { x: 28, y: 7, layer: 0 });
  return [
    ...layer0,
    ...grid(range(8, 18), range(2, 12), 1),
    ...grid(range(10, 16), range(4, 10), 2),
    ...grid(range(12, 14), range(6, 8), 3),
    { x: 13, y: 7, layer: 4 },
  ];
}

export const LAYOUTS: readonly Layout[] = [
  { id: 'flat', name: 'Flat', positions: grid(range(0, 10), range(0, 10), 0) },
  { id: 'pyramid', name: 'Pyramid', positions: [...grid(range(0, 10), range(0, 6), 0), ...grid(range(2, 8), range(2, 4), 1)] },
  { id: 'turtle', name: 'Turtle', positions: turtle() },
];

export const LAYOUT_IDS = LAYOUTS.map((l) => l.id);

export function layoutById(id: string): Layout {
  const found = LAYOUTS.find((l) => l.id === id);
  if (!found) throw new Error(`unknown mahjong solitaire layout "${id}"`);
  return found;
}
